// src/stores/TagStore.js - Zustand Store for Polymorphic Tags
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Predefined color palette for tags (matches service colors)
export const TAG_COLORS = [
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#8B5A2B', // Brown
  '#6366F1'  // Indigo
];

// Taggable entity types (matches backend TaggableType)
export const TaggableType = {
  CUSTOMER: 'customer',
  NOTE: 'note',
  DOCUMENT: 'document'
};

// Store creation with selector subscription middleware
const useTagStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // All business tags (loaded once, cached globally)
    tags: [],
    tagsLoading: false,
    tagsLoaded: false,

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS
    // ================================

    /**
     * Load all tags for the business
     */
    loadTags: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      // Don't reload if already loaded
      if (get().tagsLoaded && get().tags.length > 0) {
        console.log('TagStore: Tags already loaded, skipping');
        return;
      }

      console.log('TagStore: Loading all business tags');

      set({ tagsLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_TAGS, {});
    },

    /**
     * Create a new tag
     * @param {object} tagData - { name, color?, description? }
     */
    createTag: (tagData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      if (!tagData.name) {
        console.error('TagStore: Tag name is required');
        return;
      }

      console.log('TagStore: Creating tag:', tagData.name);

      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_TAG, {
        name: tagData.name,
        color: tagData.color || TAG_COLORS[0],
        description: tagData.description || ''
      });
    },

    /**
     * Update an existing tag
     * @param {string} tagId - Tag ID
     * @param {object} updates - { name?, color?, description? }
     */
    updateTag: (tagId, updates) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      if (!tagId) {
        console.error('TagStore: Tag ID is required');
        return;
      }

      console.log('TagStore: Updating tag:', tagId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_TAG, {
        tag_id: tagId,
        ...updates
      });
    },

    /**
     * Delete a tag
     * @param {string} tagId - Tag ID
     */
    deleteTag: (tagId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      if (!tagId) {
        console.error('TagStore: Tag ID is required');
        return;
      }

      console.log('TagStore: Deleting tag:', tagId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_TAG, {
        tag_id: tagId
      });
    },

    /**
     * Add a tag to any entity (customer, note, document)
     * @param {object} params - { taggableType, taggableId, tagName, color?, customerId? }
     */
    addTagToEntity: ({ taggableType, taggableId, tagName, color, customerId }) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      if (!taggableType || !taggableId || !tagName) {
        console.error('TagStore: taggableType, taggableId, and tagName are required');
        return;
      }

      console.log('TagStore: Adding tag to', taggableType, taggableId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.ADD_TAG_TO_ENTITY, {
        taggable_type: taggableType,
        taggable_id: taggableId,
        tag_name: tagName,
        color: color || TAG_COLORS[0],
        customer_id: customerId
      });
    },

    /**
     * Remove a tag from any entity
     * @param {object} params - { taggableType, taggableId, tagId, customerId? }
     */
    removeTagFromEntity: ({ taggableType, taggableId, tagId, customerId }) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      if (!taggableType || !taggableId || !tagId) {
        console.error('TagStore: taggableType, taggableId, and tagId are required');
        return;
      }

      console.log('TagStore: Removing tag from', taggableType, taggableId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.REMOVE_TAG_FROM_ENTITY, {
        taggable_type: taggableType,
        taggable_id: taggableId,
        tag_id: tagId,
        customer_id: customerId
      });
    },

    /**
     * Add a tag to a customer (convenience method)
     * @param {string} customerId - Customer ID
     * @param {string} tagName - Tag name
     * @param {string} color - Optional color
     */
    addTagToCustomer: (customerId, tagName, color) => {
      get().addTagToEntity({
        taggableType: TaggableType.CUSTOMER,
        taggableId: customerId,
        tagName,
        color
      });
    },

    /**
     * Remove a tag from a customer (convenience method)
     * @param {string} customerId - Customer ID
     * @param {string} tagId - Tag ID
     */
    removeTagFromCustomer: (customerId, tagId) => {
      get().removeTagFromEntity({
        taggableType: TaggableType.CUSTOMER,
        taggableId: customerId,
        tagId
      });
    },

    /**
     * Add a tag to a note (convenience method)
     * @param {string} noteId - Note ID
     * @param {string} tagName - Tag name
     * @param {string} customerId - Customer ID (for logging)
     * @param {string} color - Optional color
     */
    addTagToNote: (noteId, tagName, customerId, color) => {
      get().addTagToEntity({
        taggableType: TaggableType.NOTE,
        taggableId: noteId,
        tagName,
        color,
        customerId
      });
    },

    /**
     * Remove a tag from a note (convenience method)
     * @param {string} noteId - Note ID
     * @param {string} tagId - Tag ID
     * @param {string} customerId - Customer ID (for logging)
     */
    removeTagFromNote: (noteId, tagId, customerId) => {
      get().removeTagFromEntity({
        taggableType: TaggableType.NOTE,
        taggableId: noteId,
        tagId,
        customerId
      });
    },

    /**
     * Add a tag to a document (convenience method)
     * @param {string} documentId - Document ID
     * @param {string} tagName - Tag name
     * @param {string} customerId - Customer ID (for logging)
     * @param {string} color - Optional color
     */
    addTagToDocument: (documentId, tagName, customerId, color) => {
      get().addTagToEntity({
        taggableType: TaggableType.DOCUMENT,
        taggableId: documentId,
        tagName,
        color,
        customerId
      });
    },

    /**
     * Remove a tag from a document (convenience method)
     * @param {string} documentId - Document ID
     * @param {string} tagId - Tag ID
     * @param {string} customerId - Customer ID (for logging)
     */
    removeTagFromDocument: (documentId, tagId, customerId) => {
      get().removeTagFromEntity({
        taggableType: TaggableType.DOCUMENT,
        taggableId: documentId,
        tagId,
        customerId
      });
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get a tag by ID
     */
    getTagById: (tagId) => {
      const { tags } = get();
      return tags.find(tag => tag.id === tagId) || null;
    },

    /**
     * Get a tag by name
     */
    getTagByName: (name) => {
      const { tags } = get();
      return tags.find(tag => tag.name.toLowerCase() === name.toLowerCase()) || null;
    },

    /**
     * Search tags by partial name match
     */
    searchTags: (query) => {
      const { tags } = get();
      if (!query) return tags;
      const lowerQuery = query.toLowerCase();
      return tags.filter(tag => tag.name.toLowerCase().includes(lowerQuery));
    },

    /**
     * Force reload tags (ignores cache)
     */
    reloadTags: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('TagStore: Socket service not initialized');
        return;
      }

      console.log('TagStore: Force reloading all business tags');

      set({ tagsLoading: true, tagsLoaded: false });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_TAGS, {});
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setTagsLoaded: (data) => {
      console.log('TagStore: Tags loaded, count:', data.count);

      set({
        tags: data.tags || [],
        tagsLoading: false,
        tagsLoaded: true
      });
    },

    _addTag: (tag) => {
      console.log('TagStore: Tag created:', tag.name);

      set(state => ({
        tags: [...state.tags, tag]
      }));
    },

    _updateTag: (updatedTag) => {
      console.log('TagStore: Tag updated:', updatedTag.name);

      set(state => ({
        tags: state.tags.map(tag =>
          tag.id === updatedTag.id ? updatedTag : tag
        )
      }));
    },

    _removeTag: (data) => {
      const { id, name } = data;
      console.log('TagStore: Tag deleted:', name || id);

      set(state => ({
        tags: state.tags.filter(tag => tag.id !== id)
      }));
    },

    _handleTagAddedToEntity: (data) => {
      const { tagging, taggable_type, taggable_id } = data;
      console.log('TagStore: Tag added to', taggable_type, taggable_id);

      // If this created a new tag, add it to our tags list
      // (NoteStore/DocumentStore handle their own entity updates via their own listeners)
      if (tagging?.tag && !get().getTagById(tagging.tag.id)) {
        set(state => ({
          tags: [...state.tags, tagging.tag]
        }));
      }
    },

    _handleTagRemovedFromEntity: (data) => {
      const { taggable_type, taggable_id } = data;
      console.log('TagStore: Tag removed from', taggable_type, taggable_id);
      // NoteStore/DocumentStore handle their own entity updates via their own listeners
    },

    _setTagsLoadFailed: () => {
      console.error('TagStore: Failed to load tags');

      set({
        tagsLoading: false
      });
    },

    // ================================
    // INTERNAL METHODS
    // ================================

    _getSocketManager: () => {
      return {
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null
      };
    },

    _initialize: (socketManager) => {
      console.log('TagStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        isConnected: true,
        _initialized: true
      });

      // Auto-load tags on initialization
      get().loadTags();
    },

    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },

    _handleAuthReset: () => {
      console.log('TagStore: Resetting all state due to auth reset');

      set({
        tags: [],
        tagsLoading: false,
        tagsLoaded: false,
        isConnected: false,
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP
// ================================

export const initializeTagStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }

  console.log('TagStore: Setting up socket event listeners');

  const { universalHandler } = socketManager;

  // Set up event listeners
  const tagEvents = [
    // Success events
    SOCKET_EVENTS.TAGS_LOADED,
    SOCKET_EVENTS.TAG_CREATED,
    SOCKET_EVENTS.TAG_UPDATED,
    SOCKET_EVENTS.TAG_DELETED,
    SOCKET_EVENTS.TAG_ADDED_TO_ENTITY,
    SOCKET_EVENTS.TAG_REMOVED_FROM_ENTITY,
    SOCKET_EVENTS.TAG_ADDED_TO_CUSTOMER,
    SOCKET_EVENTS.TAG_REMOVED_FROM_CUSTOMER,
    SOCKET_EVENTS.CUSTOMER_TAGS_LOADED,
    SOCKET_EVENTS.ENTITY_TAGS_LOADED,
    // Failure events
    SOCKET_EVENTS.GET_TAGS_FAILED,
    SOCKET_EVENTS.CREATE_TAG_FAILED,
    SOCKET_EVENTS.UPDATE_TAG_FAILED,
    SOCKET_EVENTS.DELETE_TAG_FAILED,
    SOCKET_EVENTS.ADD_TAG_TO_ENTITY_FAILED,
    SOCKET_EVENTS.REMOVE_TAG_FROM_ENTITY_FAILED,
    SOCKET_EVENTS.ADD_TAG_TO_CUSTOMER_FAILED,
    SOCKET_EVENTS.REMOVE_TAG_FROM_CUSTOMER_FAILED,
    SOCKET_EVENTS.GET_ENTITY_TAGS_FAILED,
    SOCKET_EVENTS.GET_CUSTOMER_TAGS_FAILED
  ];

  tagEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      const store = useTagStore.getState();

      switch (eventName) {
        // Success events
        case SOCKET_EVENTS.TAGS_LOADED:
          store._setTagsLoaded(data);
          break;

        case SOCKET_EVENTS.TAG_CREATED:
          store._addTag(data);
          break;

        case SOCKET_EVENTS.TAG_UPDATED:
          store._updateTag(data);
          break;

        case SOCKET_EVENTS.TAG_DELETED:
          store._removeTag(data);
          break;

        case SOCKET_EVENTS.TAG_ADDED_TO_ENTITY:
        case SOCKET_EVENTS.TAG_ADDED_TO_CUSTOMER:
          store._handleTagAddedToEntity(data);
          break;

        case SOCKET_EVENTS.TAG_REMOVED_FROM_ENTITY:
        case SOCKET_EVENTS.TAG_REMOVED_FROM_CUSTOMER:
          store._handleTagRemovedFromEntity(data);
          break;

        case SOCKET_EVENTS.CUSTOMER_TAGS_LOADED:
        case SOCKET_EVENTS.ENTITY_TAGS_LOADED:
          // These are direct responses to get_customer_tags / get_entity_tags
          // Usually not needed since tags come with entities, but log for debugging
          console.log('TagStore: Entity tags loaded:', data);
          break;

        // Failure events
        case SOCKET_EVENTS.GET_TAGS_FAILED:
          store._setTagsLoadFailed();
          console.error('TagStore: Failed to load tags:', data?.message);
          break;

        case SOCKET_EVENTS.CREATE_TAG_FAILED:
        case SOCKET_EVENTS.UPDATE_TAG_FAILED:
        case SOCKET_EVENTS.DELETE_TAG_FAILED:
        case SOCKET_EVENTS.ADD_TAG_TO_ENTITY_FAILED:
        case SOCKET_EVENTS.REMOVE_TAG_FROM_ENTITY_FAILED:
        case SOCKET_EVENTS.ADD_TAG_TO_CUSTOMER_FAILED:
        case SOCKET_EVENTS.REMOVE_TAG_FROM_CUSTOMER_FAILED:
        case SOCKET_EVENTS.GET_ENTITY_TAGS_FAILED:
        case SOCKET_EVENTS.GET_CUSTOMER_TAGS_FAILED:
          console.error('TagStore: Operation failed:', data?.message);
          break;
      }
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useTagStore.getState()._handleAuthReset();
    });
  }

  // Initialize the store
  useTagStore.getState()._initialize(socketManager);

  console.log('TagStore: Socket event listeners initialized');
};

export default useTagStore;
